// util.js — shared pure helpers used across the dashboard.
//
// "Pure" means: no DOM mutation, no global state, no async side
// effects. If a function reads from `state`, mutates the document,
// or calls fetch, it belongs in the page module that owns it (or in
// src/dom.js / src/format.js for DOM/format helpers respectively).
//
// These exports are imported by BOTH src/bootstrap.js AND
// the page modules — single source of truth.

// escapeHTML converts a value to a string and escapes the five
// characters that have HTML meaning (& < > " '). Use it any time
// you interpolate user-provided or backend-provided data into an
// innerHTML template literal — without it, an event with EventType
// containing "<script>" would execute when the dashboard renders it.
//
// Returns "" for null/undefined inputs so callers don't have to
// guard.
export function escapeHTML(s) {
  if (s == null) return "";
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);
}

// parseUTC handles both RFC3339 ("2026-05-12T19:36:57Z") AND
// SQLite's `datetime('now')` format ("2026-05-12 19:36:57", space
// separator, no Z) which the Cloudflare Worker passes through
// un-normalized in several endpoints. Without the explicit Z,
// JavaScript treats the second form as local time — for a UTC user
// east of GMT that makes "now" parse as "$LOCAL_OFFSET hours in the
// future." Pre-fix the dashboard showed "last seen in the future"
// on every freshly-pushed heartbeat. Always treat timestamps with
// no zone as UTC; that matches D1 and Postgres semantics for these
// fields.
export function parseUTC(s) {
  if (!s) return NaN;
  if (typeof s !== "string") return new Date(s).getTime();
  // If it already has a zone designator (Z, +HH:MM, -HH:MM at
  // position 19+), trust the string.
  if (/[Zz]|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s).getTime();
  // SQLite "YYYY-MM-DD HH:MM:SS" → swap space for T, append Z.
  return new Date(s.replace(" ", "T") + "Z").getTime();
}

// ago renders a humanized "5s ago" / "3m ago" / "2h ago" / "4d ago"
// label for a timestamp. Handles both RFC3339 and SQLite datetime
// formats via parseUTC. Returns "--" for missing input.
//
// Tiny negative skew (clock drift / sync race) rounds up to "just
// now" rather than the alarming "in the future." Anything > 30s
// negative is treated as a real bug worth surfacing.
export function ago(iso) {
  if (!iso) return "--";
  const ts = parseUTC(iso);
  if (!Number.isFinite(ts)) return "--";
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < -30) return "in the future";
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// shortTime renders a compact timestamp for table cells:
//   - Time-only ("14:32:01") when the timestamp is from today.
//   - "MMM D HH:MM" when older.
// Saves horizontal space without being ambiguous when scrolling
// through history.
//
// MUST use parseUTC, not `new Date(iso)` directly, because the
// coordinator passes D1 timestamps through un-normalized as
// "YYYY-MM-DD HH:MM:SS" (space separator, no zone designator).
// `new Date()` on that form treats it as LOCAL time, so for a UTC
// user east of GMT a freshly-written event displays as
// "$LOCAL_OFFSET hours in the future" (e.g., 22:41 UTC →
// 22:41 PM EDT instead of 18:41 PM EDT). The same-day check
// would also fire wrong in the early-AM / late-PM hours where
// UTC and local sit on different calendar days. Fixed 2026-05-20.
export function shortTime(iso) {
  const ms = parseUTC(iso);
  if (!Number.isFinite(ms)) return "--";
  const d = new Date(ms);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }
  return (
    d.toLocaleDateString([], { month: "short", day: "numeric" }) +
    " " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
}

// formatUSD renders a dollar amount with sensible precision:
//   - 4 decimals when under $1 (matches LLM-spend granularity)
//   - 2 decimals otherwise
// Matches how Anthropic Console renders small + large totals.
export function formatUSD(amount) {
  if (amount < 1) return "$" + amount.toFixed(4);
  return (
    "$" +
    amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

// fmtDiskMB renders a megabyte count as "X MB" or "X.Y GB".
// Returns "" for null/undefined so callers don't have to guard.
export function fmtDiskMB(mb) {
  if (mb == null) return "";
  if (mb < 1024) return mb + " MB";
  return (mb / 1024).toFixed(1) + " GB";
}

// humanList renders an array as a comma-separated list with "or"
// before the last item:
//   []           → ""
//   ["a"]        → "a"
//   ["a","b"]    → "a or b"
//   ["a","b","c"] → "a, b, or c"
//
// Used in rule-sentence rendering for both Policy and Priority.
export function humanList(items) {
  if (!items || items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return items[0] + " or " + items[1];
  return (
    items.slice(0, -1).join(", ") + ", or " + items[items.length - 1]
  );
}

// formatDuration converts an integer second count into a compact
// human-readable string ("12m", "2h 14m", "—" for zero). Designed
// for the table cell, not a tooltip — full timestamps go in the
// tooltip via title=.
export function formatDuration(secs) {
  if (!secs || secs <= 0) return "—";
  if (secs < 60) return secs + "s";
  if (secs < 3600) return Math.floor(secs / 60) + "m";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return m > 0 ? h + "h " + m + "m" : h + "h";
}
